import { Box, IconButton, Typography, Stack, ButtonBase, styled } from "@mui/material"
import { useRecommendDetail } from "./RecommendDetailContext"
import { LazyLoadImage } from "react-lazy-load-image-component"
import { useOrderStore } from "../../../store/order"
import { useInfoStore } from "../../../store/userInfo"
import { useRef, useState, useMemo, useEffect } from "react"
import { isAndroid } from "react-device-detect"
import { useMenuStore } from "../../../store/menuState"
import { useTranslation } from "react-i18next"
import Modifier from "./Modifier"
import ModifierStatusList from "./ModifierStatus"
import OrderBox from "./OrderBox"
import { IDetail } from "../../../types/menu";
import { useRecommendStore } from "../../../store/recommend";

const IMAGE_URL = process.env.REACT_APP_S3_IMAGE_PAPH;

const LazyPicture = styled(LazyLoadImage as any)`
  object-fit: cover;
  width: ${({ width }: { width?: string }) => (width ? `${width}rem` : "100%")};
  height: ${({ width }: { width?: string }) => (width ? `${width}rem` : "100%")};
  border-radius: 1.5rem;
`;

/**
 * MIME 타입 추출 함수
 */
const getMimeType = (imagePath: string): string => {
  const extension = imagePath.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "webp":
      return "image/webp";
    case "gif":
      return "image/gif";
    default:
      return "application/octet-stream"; // 기본 MIME 타입
  }
};

export const RecommendLandscape = () => {
  const { recommendItem, setRecommendItem, handleClose, handleAddCount, handleSubCount, handleAddtoCart, checkLimitCountText, selectRequired, setSelectRequired } = useRecommendDetail();
  const { preRecommendOrder, setPreRecommendOrder } = useOrderStore();
  const { userInfo, tableInfo, currentLng } = useInfoStore();
  const { setSnackMode, setShowSnackbar, setLimitValue } = useOrderStore();
  const storeId = userInfo?.storeId;
  const isAyce = userInfo?.isAyce;
  const { t } = useTranslation(`${storeId}`);
  const { menuDao } = useMenuStore();

  // 네이티브 이미지 캐시 관련 ref 및 강제 리렌더링용 상태
  const base64CacheRef = useRef(new Map<string, string>());
  const [nativeImageVersion, setNativeImageVersion] = useState(0);
  const [imageSrc, setImageSrc] = useState('')

  /**
   * menuDao를 ID 기준으로 매핑
   */
  const menuMap = useMemo(() => {
    const map = new Map();
    menuDao.forEach((menu: any) => {
      map.set(menu.id, menu);
    });
    return map;
  }, [menuDao]);


  const TRANSPARENT_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
  const defaultImage = "/assets/images/no_image.png";
  const getBase64ImageSrc = (imagePath: string | null): string => {
    if (!imagePath) return defaultImage;
    if (base64CacheRef.current.has(imagePath)) {
      return base64CacheRef.current.get(imagePath)!;
    }

    // 네이티브 Android 호출을 비동기 처리하여 캐시 업데이트 및 재렌더 유도
    setTimeout(() => {
      const base64Image = window.Android?.getImageAsBase64(imagePath);
      if (base64Image) {
        const mimeType = getMimeType(imagePath);
        const src = `data:${mimeType};base64,${base64Image}`;
        base64CacheRef.current.set(imagePath, src);
        // 강제 리렌더를 위해 상태 업데이트 (nativeImageVersion 값은 사용하지 않음)
        setNativeImageVersion(v => v + 1);
      }
    }, 0);
    return TRANSPARENT_IMAGE;
  };

  useEffect(() => {
    if (recommendItem) {
      const menuItem = menuMap.get(recommendItem.itemId);
      const webImagePath = IMAGE_URL + `/${recommendItem.images[0]}`;
      const androidImagePath = menuItem?.images?.[0] ?? null;
      const src = isAndroid
        ? (androidImagePath ? getBase64ImageSrc(androidImagePath) : webImagePath)
        : webImagePath;
      setImageSrc(src);
    }
  }, [recommendItem, nativeImageVersion]);

  useEffect(() => {
    if (recommendItem) {
      const hasRequiredModifiers = recommendItem.modifierGroups?.some(
        group => group.isRequired && group.modifiers.some(modifier => (modifier.quantity || 0) > 0)
      );
      setSelectRequired(hasRequiredModifiers || !recommendItem.hasModifier);
    }
  }, [recommendItem, setSelectRequired]);

  useEffect(() => {
    if (recommendItem) {
      setPreRecommendOrder({
        ...recommendItem,
        quantity: 1,
        orderModifiers: [],
        isRecommend: true,
        itemWithModifierPrice: recommendItem.itemPrice,
        newItem: true
      });
    }
  }, []);

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 0) return;
    if(recommendItem!.minRequired && newQuantity < recommendItem!.minRequired) {
      // toast.info(`This can be down to ${itemDetail!.minRequired} items.`)
      setLimitValue(recommendItem!.minRequired)
      setSnackMode(13)
      setShowSnackbar(true)
      return
    }
    const updatedItem = {
      ...recommendItem,
      quantity: newQuantity
    };
    setRecommendItem(updatedItem as IDetail);
  };

  // const handleAddCount = (item: ICart) => {
  //   if(!recommendItem) return;
  //   if(isAyce) {
  //     if(item!.maxAllowedPerTable === 0) {
  //       setLimitValue(item!.maxAllowedPerTable)
  //       setSnackMode(12)
  //       setShowSnackbar(true)
  //       return
  //     }
  //     if(recommendItem.maxAllowedPerTable) {
  //       const itemQuantity = recommendItem.quantity >= recommendItem.maxAllowedPerTable ? recommendItem.quantity : recommendItem.quantity+1;
  //       setPreRecommendOrder({...item, quantity: itemQuantity});
  //       setRecommendItem({...recommendItem, quantity: itemQuantity});
  //     } else {
  //       setPreRecommendOrder({...item, quantity: item.quantity + 1});
  //       setRecommendItem({...recommendItem, quantity: recommendItem.quantity + 1});
  //     }
  //   } else {
  //     setPreRecommendOrder({...item, quantity: item.quantity + 1});
  //     setRecommendItem({...recommendItem, quantity: recommendItem.quantity + 1});
  //   }
  // };

  // const handleSubCount = (item: ICart) => {
  //   if(!recommendItem) return;
    
  //   // 최소 수량 체크
  //   if(recommendItem.minRequired && recommendItem.quantity <= recommendItem.minRequired) {
  //     setLimitValue(recommendItem.minRequired);
  //     setSnackMode(13);
  //     setShowSnackbar(true);
  //     return;
  //   }

  //   const itemQuantity = recommendItem.quantity - 1;
  //   setPreRecommendOrder({...item, newItem: true, quantity: itemQuantity});
  //   setRecommendItem({...recommendItem, quantity: itemQuantity});
  // };

  if (!recommendItem) return null;

  return (
    <Box className="dialog-content" onClick={(e) => e.stopPropagation()}>
      <Box sx={{background: '#fff', flexDirection: 'row', width: '100%', height: '6.8rem', borderRadius: '1.5rem 1.5rem'}}>
        <div className="touch-area" onClick={handleClose}>
          <IconButton aria-label="close" style={{position: 'absolute', right: '3.0rem', top: '0rem', width: '6.6rem', height: '6.6rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}} >
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <LazyPicture style={{position: 'absolute',top: 0, left: 0, width: '100%', height: '100%', }}
                effect="opacity"
                src={process.env.PUBLIC_URL+'/assets/images/close_bg.svg'}
              />
              <LazyPicture style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '2.0rem', height: '2.0rem',}}
                effect="opacity"
                src={process.env.PUBLIC_URL+'/assets/images/close_w.svg'}
              />
            </div>
          </IconButton>
        </div>
      </Box>
      <Box sx={{width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'hidden'}}>
        {recommendItem.hasModifier && <Box sx={{width: '18.0rem'}}>
          <ModifierStatusList itemDetail={recommendItem} setItemDetail={() => {}} />
        </Box>}
        <Box sx={{flex: 1, p: recommendItem.hasModifier? '0rem 1.0rem 0rem 2.0rem': '0rem 5.0rem 1.0rem 5.0rem', overflow: 'hidden'}}>
          <Box sx={{ height: `calc(${window.innerHeight/10}rem - ${recommendItem.hasModifier ? 4.9: 8.9}rem)`, mt: '1.0rem'}}>
            <Box sx={{overflow: 'auto', overflowX: 'hidden', pr: '1.0rem', maxHeight: 'calc(100% - 10.0rem)' }}>
              <Box sx={{display: 'flex', flexDirection: 'row', gap: '2.0rem', mb: '2.0rem'}}>
                { (!recommendItem?.images?.[0]? 
                  <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
                    <Box sx={{ width: recommendItem.hasModifier ? '407px': `644px`,  height: recommendItem.hasModifier ? '266px' : `362px`, borderRadius: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#ccc'}}>
                      <Typography sx={{fontSize: '3.0rem',  textAlign: 'left', fontWeight: '500', color: 'rgba(255, 255, 255, 0.80)',  letterSpacing: '-.03rem'}}>
                        {t(`${storeId}:itemNames.${recommendItem.itemId}.${currentLng}`)}
                      </Typography>
                    </Box>
                  </Box>
                  :
                  <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
                    <LazyPicture sx={{width: recommendItem.hasModifier ? '407px': `644px`, height: recommendItem.hasModifier ? '266px' : `362px`}}
                      effect="opacity"
                      src={imageSrc}
                    />
                  </Box>
                )}
                {recommendItem.hasModifier ? <Stack sx={{display: 'flex', width:  '69.2rem', justifyContent: 'flex-start', alignItems: 'flex-start',}}>
                  <Typography id="item-title" sx={{ fontSize: '2.6rem', fontWeight: '600', fontStyle:'normal', letterSpacing: '-.024rem' , maxHeight: '7.2rem'}}>
                    {t(`${storeId}:itemNames.${recommendItem.itemId}.${currentLng}`)}
                  </Typography>
                  <Typography id="item-price" sx={{fontSize:recommendItem.isPriceVisible? '2.2rem': '1.8rem', fontWeight: '600', fontStyle:'normal', height: '3.0rem', textAlign: 'left', my: '.4rem' }}>
                    {recommendItem.isPriceVisible? `$${(recommendItem?.itemPrice/100).toFixed(2)}` : "Priced by add-ons"}
                  </Typography>
                  {recommendItem.enableDesc && <Box sx={{width: '100%', height: '16.8rem', overflow:'overlay', overflowX: 'hidden', mt: '.5rem', mr: '.5rem', textWrap: 'wrap'}}>
                    <Typography id="item-price" sx={{fontSize: '2.0rem', whiteSpace: 'break-spaces', fontWeight: '400', fontStyle: 'normal', textWrap: 'wrap'}}>
                      {t(`itemDescs.${recommendItem.itemId}.${currentLng}`)}
                    </Typography>
                  </Box>}
                </Stack> : 
                <Stack sx={{width: '50.5rem', justifyContent: 'flex-end', alignItems: 'flex-start'}}>
                  <Typography id="item-title" sx={{ fontSize: '3.0rem', fontWeight: '600', fontStyle:'normal', lineHeight: '3.0rem', letterSpacing: '-.024rem', width: '50.5rem', maxHeight: '7.2rem'}}>
                    {t(`${storeId}:itemNames.${recommendItem.itemId}.${currentLng}`)}
                  </Typography>
                  <Typography id="item-price" sx={{fontSize: recommendItem.isPriceVisible? '2.2rem': '1.8rem', fontWeight: '600', fontStyle:'normal', width: '8.3rem', height: '3.0rem', textAlign: 'left', my: '.4rem' }}>
                    {recommendItem.isPriceVisible? `$${(recommendItem?.itemPrice/100).toFixed(2)}` : "Priced by add-ons"}
                  </Typography>
                </Stack>}
              </Box>
              {!recommendItem.hasModifier && recommendItem.enableDesc && <Box sx={{width: '100%', height: '16.8rem', overflow:'overlay', overflowX: 'hidden', mt: '.5rem'}}>
                <Typography id="item-price" sx={{fontSize: '2.2rem', whiteSpace: 'pre-line', fontWeight: '400', fontStyle: 'normal'}}>
                  {t(`itemDescs.${recommendItem.itemId}.${currentLng}`)}
                </Typography>
              </Box>}
              {recommendItem.hasModifier ?
                <Modifier 
                  recommendItem={recommendItem} 
                  setRecommendItem={setRecommendItem} 
                  setSelectRequired={setSelectRequired}
                  checkedModifierPrice={0} 
                />
                : <Box></Box>
              }
            </Box>
          </Box>
          {!recommendItem.hasModifier && (
            (recommendItem.useAgeVerification && recommendItem.requiresAgeVerification && !recommendItem.isAgeVerified) ? 
              <Box sx={{position: 'fixed', bottom: '0rem', width: '100%'}}>
                <Box sx={{display: 'flex', flexDirection:'row', justifyContent: 'center', alignItems: 'flex-start', background: '#fff' , width: '100%', borderRadius: 0, mt: '3.0rem', mb: '3.0rem',  height: '5.5rem'}}>
                  {/* <LazyPicture style={{marginRight: '1.3rem', marginLeft: '.4rem', width: '4.0rem', height: '4.0rem'}}
                    effect="opacity"
                    src={process.env.PUBLIC_URL+'/assets/images/21+.svg'}
                  /> */}
                  <Typography sx={{fontSize: '2.4rem', fontWeight:'600'}}>Please ask your server to order this item.</Typography>
                </Box>
              </Box> :
              <Box sx={{position: 'fixed', bottom: '0rem', width: '100%'}}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start', width: '100%', borderRadius: 0, pt: '3.0rem', pb: '3.0rem', }}>
                  {(recommendItem.maxAllowed || recommendItem.minRequired! > 1) &&
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', mr: '1.0rem', alignItems: 'center', height: '5.5rem' }}>
                      <LazyPicture style={{width: '3.9rem', height: '3.9rem'}}
                        effect="opacity"
                        src={process.env.PUBLIC_URL+'/assets/images/warning_r.svg'}
                      />
                      <Typography sx={{fontSize: '2.6rem', fontWeight: '600', color: '#F72E13'}}>{checkLimitCountText()}</Typography>
                    </Box>
                  }
                  {recommendItem.quantity !== 0 && <>
                    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width:'17.2rem', height:'5.5rem',
                            padding: '.4rem 1.4rem', borderRadius: '5.5rem', backgroundColor: '#fff', border: '2px solid #E9E9E9', gap: '2.0rem'}}>
                      <IconButton sx={{p : '0rem'}}
                        aria-label="minus"
                        onClick={() => handleQuantityChange((recommendItem.quantity || 0) - 1)}
                      >
                        <LazyPicture style={{background: '#fff', width: '2.6rem' , height: '2.6rem'}}
                          effect="opacity"
                          src={process.env.PUBLIC_URL+'/assets/images/minus.svg'}
                        />
                      </IconButton>
                      <Typography id="item-price" sx={{fontSize: '2.2rem', fontWeight: '700', fontStyle: 'normal', width: '6.0rem', lineHeight: 'normal', textAlign: 'center'}}>
                        {recommendItem.quantity}
                      </Typography>
                      <IconButton  sx={{p : '0rem'}}
                        aria-label="plus"
                        onClick={() => handleQuantityChange((recommendItem.quantity || 0) + 1)}
                      >
                        <LazyPicture style={{background: '#fff', width:  '2.6rem' , height: '2.6rem'}}
                          effect="opacity"
                          src={process.env.PUBLIC_URL+'/assets/images/add.svg'}
                        />
                      </IconButton>
                    </Box>
                    <ButtonBase sx={{width: '26.3rem', padding: '.6rem 1.5rem', justifyContent: 'center', height: '5.5rem', backgroundColor: '#DA291C', borderRadius: '5.5rem', ml: '1.4rem'}} 
                        onClick={(e) => handleAddtoCart(e, recommendItem)}>
                        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
                          <Typography sx={{fontSize: '20px', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.022rem', textTransform:' none', width: 'auto', flex: '0 0 auto', flexWrap: 'nowrap', color: '#fff', textAlign: 'center', textWrap: 'noWrap'}}>Add to Cart</Typography>
                          <Typography sx={{fontSize: '20px', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.022rem', textTransform:' none', width: '8.0rem', color: '#fff', textAlign: 'end', ml: '.5rem'}}>${(recommendItem.quantity * recommendItem.itemPrice/100).toFixed(2)}</Typography>
                        </Box>
                    </ButtonBase>
                  </>}
                </Box>
              </Box>
          )}
        </Box>
        {recommendItem.hasModifier && <Box sx={{width: '33.0rem'}}>
          <OrderBox 
            setOpenOrderBox={false}
            checkLimitText={checkLimitCountText}
            selectRequired={selectRequired}
            setSelectRequired={setSelectRequired}
          />
        </Box>}
      </Box>
    </Box>
  );
};

export default RecommendLandscape;